import type { Patient, Reference } from '@medplum/fhirtypes'
import { codeableConcept } from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import {
  DIASTOLIC_BLOOD_PRESSURE_CODING,
  SYSTOLIC_BLOOD_PRESSURE_CODING,
} from '@seen/core/models/loinc'
import { ExtendedObservation } from '@seen/core/models/observation'
import { type JSX, useCallback, useRef } from 'react'
import { toast } from 'sonner'
import { FirstMatchDecorator } from '#src/components/rich-text-editor/ObservationNode/FirstMatchDecorator.ts'
import { ObservationNodeInlineDisplay } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeInlineDisplay.tsx'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDebouncedCallback } from '#src/hooks/useDebouncedCallback.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function ObservationNodeBloodPressure({
  attributes,
  updateAttributes,
  participant,
  onReferencePersisted,
  disabled,
}: {
  attributes: ObservationNodeAttributes
  updateAttributes: (attributes: Partial<ObservationNodeAttributes>) => void
  participant: Reference<Patient> | undefined
  onReferencePersisted?: () => void
  disabled?: boolean
}): JSX.Element {
  const isReadOnly = useIsReadOnly()
  const { data: observation } = useRead(
    ExtendedObservation,
    attributes.reference
  )

  const createObservationPromiseRef =
    useRef<Promise<ExtendedObservation> | null>(null)

  const createMutation = useCreateMutation<ExtendedObservation>()
  const practitioner = useCurrentPractitioner()
  const { mutateAsync: patchObservation } =
    usePatchMutation<ExtendedObservation>()

  const updateBloodPressure = useCallback(
    async (observation: ExtendedObservation, html: string) => {
      const { diastolic, systolic } = extractBloodPressureFromHtml(html)
      try {
        await patchObservation({
          instance: observation,
          mutateFn: (o) => {
            if (diastolic !== undefined && systolic !== undefined) {
              o.component = [
                {
                  code: SYSTOLIC_BLOOD_PRESSURE_CODING,
                  valueQuantity: {
                    value: systolic,
                    unit: 'mmHg',
                    system: 'http://unitsofmeasure.org',
                    code: 'mm[Hg]',
                  },
                },
                {
                  code: DIASTOLIC_BLOOD_PRESSURE_CODING,
                  valueQuantity: {
                    value: diastolic,
                    unit: 'mmHg',
                    system: 'http://unitsofmeasure.org',
                    code: 'mm[Hg]',
                  },
                },
              ]
            } else {
              o.component = undefined
            }
            o.setNoteFirst(html, practitioner.reference)
          },
        })
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? `Failed to update blood pressure: ${error.message}`
            : `Failed to update blood pressure`
        toast.error(errorMessage)
        throw error
      }
    },
    [patchObservation, practitioner.reference]
  )

  const debouncedOnChange = useDebouncedCallback(async (html) => {
    if (!attributes.reference) {
      if (createObservationPromiseRef.current) {
        // Wait for the existing createObservationPromiseRef to finish before proceeding
        const createdObservation = await createObservationPromiseRef.current
        await updateBloodPressure(createdObservation, html)
      } else {
        const { diastolic, systolic } = extractBloodPressureFromHtml(html)
        if (diastolic === undefined || systolic === undefined) {
          toast.error(`Invalid blood pressure values.`)
          return
        }
        createObservationPromiseRef.current = createMutation.mutateAsync(
          ExtendedObservation.fromComponent({
            component: [
              {
                code: SYSTOLIC_BLOOD_PRESSURE_CODING,
                valueQuantity: {
                  value: systolic,
                  unit: 'mmHg',
                  system: 'http://unitsofmeasure.org',
                  code: 'mm[Hg]',
                },
              },
              {
                code: DIASTOLIC_BLOOD_PRESSURE_CODING,
                valueQuantity: {
                  value: diastolic,
                  unit: 'mmHg',
                  system: 'http://unitsofmeasure.org',
                  code: 'mm[Hg]',
                },
              },
            ],
            code: codeableConcept({
              code: attributes.code,
              system: attributes.system,
              display: attributes.codingDisplay,
            }),
            participant,
            practitioner: practitioner.reference,
            note: html,
          })
        )
        let createdObservation: ExtendedObservation
        try {
          createdObservation = await createObservationPromiseRef.current
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? `Failed to submit blood pressure: ${error.message}`
              : `Failed to submit blood pressure`
          toast.error(errorMessage)
          throw error
        } finally {
          createObservationPromiseRef.current = null
        }
        updateAttributes({
          reference: createdObservation.reference.reference,
        })
        onReferencePersisted?.()
      }
    } else {
      invariant(observation, 'observation is required')
      await updateBloodPressure(observation, html)
    }
  }, 500) // Reduced from 2500ms to 500ms to mitigate race condition where users submit timeline notes before observation reference is created. Still debounced to avoid excessive version history entries, and we flush on blur as well.

  const handleAutofocused = useCallback(() => {
    updateAttributes({ autofocus: false })
  }, [updateAttributes])

  return (
    <ObservationNodeInlineDisplay
      value={observation?.noteFirstText ?? ''}
      observationCode={attributes.code}
      observationDisplay={attributes.codingDisplay}
      debouncedOnChange={debouncedOnChange}
      additionalExtensions={additionalExtensions}
      autofocus={attributes.autofocus}
      onAutofocused={handleAutofocused}
      disabled={disabled || isReadOnly}
      noValue={disabled && !attributes.reference}
    />
  )
}

const BLOOD_PRESSURE_REGEX = /\b(\d{2,3})\s*\/\s*(\d{2,3})\b/

const additionalExtensions = [
  FirstMatchDecorator.configure({
    regex: BLOOD_PRESSURE_REGEX,
    className: 'font-mono tabular-nums text-foreground',
  }),
]

interface ExtractBloodPressureFromHtmlResult {
  systolic: number | undefined
  diastolic: number | undefined
}

function extractBloodPressureFromHtml(
  html: string
): ExtractBloodPressureFromHtmlResult {
  // Strip HTML tags and normalize whitespace
  const text = html
    .replace(/<[^>]+>/g, ' ') // remove tags
    .replace(/\s+/g, ' ') // collapse whitespace
    .trim()

  const match = text.match(BLOOD_PRESSURE_REGEX)

  if (!match) {
    return { systolic: undefined, diastolic: undefined }
  }

  const systolic = Number(match[1])
  const diastolic = Number(match[2])

  return { systolic, diastolic }
}
